import type {
  SizingConfig,
  HaModel,
  RedundancyModel,
  DRTier,
  StorageArchitectureType,
  Environment,
} from '../../types';
import { useProjectStore } from '../../store/projectStore';
import { FieldTooltip } from '../ui/FieldTooltip';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function pctToDisplay(value: number): string {
  return (value * 100).toFixed(1);
}

function displayToPct(value: string): number {
  const n = parseFloat(value);
  return isNaN(n) ? 0 : n / 100;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface SectionProps {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

function Section({ title, defaultOpen = false, children }: SectionProps) {
  return (
    <details open={defaultOpen} style={{ borderBottom: '1px solid rgba(76,92,104,0.35)', marginBottom: 0 }}>
      <summary data-panel-header style={{
        padding: '12px 16px',
        cursor: 'pointer',
        fontWeight: 600,
        fontSize: '11px',
        listStyle: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        userSelect: 'none',
        color: '#4C5C68',
        textTransform: 'uppercase',
        letterSpacing: '0.8px',
        backgroundColor: '#132840',
        fontFamily: "'Poppins', sans-serif",
      }}>
        {title}
      </summary>
      <div style={{ padding: '4px 16px 16px' }}>
        {children}
      </div>
    </details>
  );
}

interface FieldProps {
  label: string;
  hint?: string;
  tooltip?: string;
  children: React.ReactNode;
}

function Field({ label, hint, tooltip, children }: FieldProps) {
  return (
    <div style={{ marginBottom: '12px' }}>
      <label style={{ display: 'flex', alignItems: 'center', fontSize: '13px', fontWeight: 500, marginBottom: '4px', color: '#8FA4B5' }}>
        {label}
        {hint && (
          <span style={{ fontWeight: 400, color: '#4C5C68', marginLeft: '6px', fontSize: '12px' }}>
            {hint}
          </span>
        )}
        {tooltip && <FieldTooltip text={tooltip} />}
      </label>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: '200px',
  padding: '6px 8px',
  border: '1px solid rgba(76,92,104,0.35)',
  borderRadius: '6px',
  fontSize: '13px',
  color: '#E8EDF2',
  backgroundColor: '#0a1929',
  boxSizing: 'border-box',
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  maxWidth: '260px',
  cursor: 'pointer',
};

interface RadioGroupProps<T extends string> {
  name: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}

function RadioGroup<T extends string>({ name, value, options, onChange }: RadioGroupProps<T>) {
  return (
    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
      {options.map(opt => {
        const isSelected = value === opt.value;
        return (
          <label
            key={opt.value}
            {...(isSelected ? { 'data-radio-selected': '' } : {})}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '13px',
              cursor: 'pointer',
              color: isSelected ? '#F5A621' : '#E8EDF2',
              backgroundColor: isSelected ? 'rgba(245,166,33,0.12)' : 'transparent',
              border: isSelected ? '1px solid rgba(245,166,33,0.5)' : '1px solid rgba(76,92,104,0.35)',
              borderRadius: '6px',
              padding: '6px 12px',
            }}
          >
            <input
              type="radio"
              name={name}
              value={opt.value}
              checked={isSelected}
              onChange={() => onChange(opt.value)}
              style={{ display: 'none' }}
            />
            {opt.label}
          </label>
        );
      })}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function SizingConfigPanel() {
  const project = useProjectStore(s => s.project);
  const updateSizingConfig = useProjectStore(s => s.updateSizingConfig);

  if (!project) return null;

  const activeScenario = project.scenarios.find(s => s.id === project.activeScenarioId);
  if (!activeScenario) return null;

  const config = activeScenario.sizingConfig;

  function update(partial: Partial<SizingConfig>) {
    updateSizingConfig(partial);
  }

  function updateEnvOverride(env: Environment, field: 'vcpuOvercommitRatio' | 'memoryOvercommitRatio', rawValue: string) {
    const n = parseFloat(rawValue);
    if (isNaN(n) || n <= 0) return;
    const current = config.environmentOverrides ?? {};
    update({
      environmentOverrides: {
        ...current,
        [env]: {
          ...current[env],
          [field]: n,
        },
      },
    });
  }

  function clearEnvOverride(env: Environment, field: 'vcpuOvercommitRatio' | 'memoryOvercommitRatio') {
    const current = config.environmentOverrides ?? {};
    const envEntry = current[env] ?? {};
    const rest = Object.fromEntries(
      Object.entries(envEntry).filter(([k]) => k !== field),
    ) as Partial<Pick<SizingConfig, 'vcpuOvercommitRatio' | 'memoryOvercommitRatio'>>;
    const nextEnv = Object.keys(rest).length === 0
      ? (({ [env]: _e, ...r }) => r)(current)
      : { ...current, [env]: rest };
    update({ environmentOverrides: Object.keys(nextEnv).length === 0 ? undefined : nextEnv });
  }

  const environments: { key: Environment; label: string }[] = [
    { key: 'production', label: 'Production' },
    { key: 'pre_production', label: 'Pre-Production' },
    { key: 'development', label: 'Development' },
  ];

  return (
    <div style={{
      border: '1px solid rgba(76,92,104,0.35)',
      borderRadius: '8px',
      overflow: 'hidden',
      backgroundColor: '#0d1f30',
      fontSize: '14px',
    }}>
      {/* ── Compute Ratios ─────────────────────────────────────────────────── */}
      <Section title="Compute Ratios" defaultOpen>
        <Field label="vCPU Overcommit Ratio" hint="(e.g. 4 = 4 vCPU per physical core)" tooltip="Number of vCPUs allocated per physical core. A ratio of 4 means four vCPUs share one physical core. Higher ratios increase density but reduce per-VM CPU headroom. Applied before platform overhead.">
          <input
            type="number"
            min={1}
            max={32}
            step={0.5}
            value={config.vcpuOvercommitRatio}
            onChange={e => {
              const n = parseFloat(e.target.value);
              if (!isNaN(n) && n > 0) update({ vcpuOvercommitRatio: n });
            }}
            style={inputStyle}
          />
        </Field>

        <Field label="Memory Overcommit Ratio" hint="(1 = no overcommit)" tooltip="Memory allocation multiplier. A ratio of 1 means no overcommit (1 GB allocated = 1 GB physical). Most production environments use 1.0–1.25 for memory. Applied before platform overhead.">
          <input
            type="number"
            min={1}
            max={4}
            step={0.1}
            value={config.memoryOvercommitRatio}
            onChange={e => {
              const n = parseFloat(e.target.value);
              if (!isNaN(n) && n > 0) update({ memoryOvercommitRatio: n });
            }}
            style={inputStyle}
          />
        </Field>

        <Field label="Platform Overhead" hint="(% applied once, after overcommit)" tooltip="Percentage of physical resources reserved for the hypervisor, management agents, and OS kernel. Applied once, after overcommit, to avoid double-counting with vendor-supplied headroom figures.">
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <input
              type="number"
              min={0}
              max={50}
              step={0.5}
              value={pctToDisplay(config.platformOverheadPct)}
              onChange={e => update({ platformOverheadPct: displayToPct(e.target.value) })}
              style={inputStyle}
            />
            <span style={{ fontSize: '13px', color: '#4C5C68' }}>%</span>
          </div>
        </Field>
      </Section>

      {/* ── High Availability ──────────────────────────────────────────────── */}
      <Section title="High Availability" defaultOpen>
        <Field label="HA Model" tooltip="N+1 adds one spare host to the cluster so one host failure does not affect capacity. N+2 tolerates two simultaneous host failures. The spare hosts are included in the BOM host count.">
          <RadioGroup<HaModel>
            name="haModel"
            value={config.haModel}
            options={[
              { value: 'n_plus_1', label: 'N+1 (one spare host)' },
              { value: 'n_plus_2', label: 'N+2 (two spare hosts)' },
            ]}
            onChange={v => update({ haModel: v })}
          />
        </Field>

        <Field label="Redundancy Model" tooltip="Active-Passive: one site handles all traffic; the other is on standby. Active-Active: both sites handle live traffic simultaneously. Affects how DR compute is sized.">
          <RadioGroup<RedundancyModel>
            name="redundancyModel"
            value={config.redundancyModel}
            options={[
              { value: 'active_passive', label: 'Active-Passive' },
              { value: 'active_active', label: 'Active-Active' },
            ]}
            onChange={v => update({ redundancyModel: v })}
          />
        </Field>
      </Section>

      {/* ── Disaster Recovery ─────────────────────────────────────────────── */}
      <Section title="Disaster Recovery" defaultOpen>
        <Field label="DR Tier" tooltip="Cold: no compute at DR — VMs are restored from backup on demand. Warm: reduced compute sized at DR. Hot: full compute mirrored at DR. DR storage is always fully replicated regardless of tier.">
          <RadioGroup<DRTier>
            name="drTier"
            value={config.drTier}
            options={[
              { value: 'cold', label: 'Cold (no DR compute)' },
              { value: 'warm', label: 'Warm (reduced DR compute)' },
              { value: 'hot', label: 'Hot (full DR compute)' },
            ]}
            onChange={v => update({ drTier: v })}
          />
        </Field>

        {config.drTier !== 'cold' && (
          <Field label="DR Compute Fraction" hint="(% of primary compute to size at DR)" tooltip="Percentage of primary compute to size at the DR site. Only shown for Warm DR. 50% means the DR cluster is sized at half the primary cluster's capacity.">
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <input
                type="number"
                min={10}
                max={100}
                step={5}
                value={(config.drComputeFractionPct * 100).toFixed(0)}
                onChange={e => {
                  const n = parseFloat(e.target.value);
                  if (!isNaN(n) && n > 0 && n <= 100) update({ drComputeFractionPct: n / 100 });
                }}
                style={inputStyle}
              />
              <span style={{ fontSize: '13px', color: '#4C5C68' }}>%</span>
            </div>
          </Field>
        )}
      </Section>

      {/* ── Storage ───────────────────────────────────────────────────────── */}
      <Section title="Storage" defaultOpen>
        <Field label="Storage Architecture" tooltip="Determines how storage capacity is presented in the BOM. Traditional SAN: shared block storage only. Traditional NAS: shared file storage only. Converged HCI: compute and storage on the same nodes. Hybrid: a combination.">
          <select
            value={config.storageArchitecture}
            onChange={e => update({ storageArchitecture: e.target.value as StorageArchitectureType })}
            style={selectStyle}
          >
            <option value="traditional_san">Traditional SAN</option>
            <option value="traditional_nas">Traditional NAS</option>
            <option value="converged_hci">Converged HCI</option>
            <option value="hybrid">Hybrid</option>
          </select>
        </Field>

        <Field label="Usable-to-Raw Ratio" hint="(e.g. 1.33 for RAID5)" tooltip="Ratio of raw disk capacity required to deliver the requested usable capacity after RAID or erasure coding overhead. RAID5 ≈ 1.33×, RAID6 ≈ 1.5×, 3-way mirror ≈ 3×.">
          <input
            type="number"
            min={1}
            max={4}
            step={0.01}
            value={config.storageUsableToRawRatio}
            onChange={e => {
              const n = parseFloat(e.target.value);
              if (!isNaN(n) && n >= 1) update({ storageUsableToRawRatio: n });
            }}
            style={inputStyle}
          />
        </Field>

        <Field label="Storage Growth Buffer" hint="(% added to usable before conversion)" tooltip="Percentage added to usable capacity before the usable-to-raw conversion. Accounts for data growth over the contract term. Applied once — do not also add growth in the VM disk sizes.">
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <input
              type="number"
              min={0}
              max={100}
              step={5}
              value={pctToDisplay(config.storageGrowthBufferPct)}
              onChange={e => update({ storageGrowthBufferPct: displayToPct(e.target.value) })}
              style={inputStyle}
            />
            <span style={{ fontSize: '13px', color: '#4C5C68' }}>%</span>
          </div>
        </Field>

        <Field label="RAID Write Penalty Factor" hint="(e.g. 4 for RAID5, 6 for RAID6)" tooltip="Write amplification caused by RAID parity recalculation. RAID5 = 4 (each write becomes 4 I/Os), RAID6 = 6. Used with the VM read/write ratio to derive effective IOPS demand from raw IOPS figures.">
          <input
            type="number"
            min={1}
            max={10}
            step={1}
            value={config.raidWritePenaltyFactor}
            onChange={e => {
              const n = parseFloat(e.target.value);
              if (!isNaN(n) && n >= 1) update({ raidWritePenaltyFactor: n });
            }}
            style={inputStyle}
          />
        </Field>
      </Section>

      {/* ── Per-Environment Overrides ──────────────────────────────────────── */}
      <Section title="Per-Environment Overrides">
        <p style={{ fontSize: '12px', color: '#8FA4B5', marginTop: 0, marginBottom: '12px' }}>
          Override vCPU and memory overcommit ratios per environment. Leave blank to inherit the global ratio.
        </p>
        {environments.map(({ key, label }) => {
          const override = config.environmentOverrides?.[key];
          return (
            <div key={key} style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px', color: '#E8EDF2' }}>
                {label}
              </div>
              <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: '#8FA4B5', marginBottom: '2px' }}>
                    vCPU Overcommit
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <input
                      type="number"
                      min={1}
                      max={32}
                      step={0.5}
                      placeholder={String(config.vcpuOvercommitRatio)}
                      value={override?.vcpuOvercommitRatio ?? ''}
                      onChange={e => {
                        if (e.target.value === '') {
                          clearEnvOverride(key, 'vcpuOvercommitRatio');
                        } else {
                          updateEnvOverride(key, 'vcpuOvercommitRatio', e.target.value);
                        }
                      }}
                      style={{ ...inputStyle, maxWidth: '120px' }}
                    />
                    {override?.vcpuOvercommitRatio != null && (
                      <button
                        type="button"
                        onClick={() => clearEnvOverride(key, 'vcpuOvercommitRatio')}
                        style={{ fontSize: '11px', color: '#E05555', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px' }}
                        title="Clear override"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: '#8FA4B5', marginBottom: '2px' }}>
                    Memory Overcommit
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <input
                      type="number"
                      min={1}
                      max={4}
                      step={0.1}
                      placeholder={String(config.memoryOvercommitRatio)}
                      value={override?.memoryOvercommitRatio ?? ''}
                      onChange={e => {
                        if (e.target.value === '') {
                          clearEnvOverride(key, 'memoryOvercommitRatio');
                        } else {
                          updateEnvOverride(key, 'memoryOvercommitRatio', e.target.value);
                        }
                      }}
                      style={{ ...inputStyle, maxWidth: '120px' }}
                    />
                    {override?.memoryOvercommitRatio != null && (
                      <button
                        type="button"
                        onClick={() => clearEnvOverride(key, 'memoryOvercommitRatio')}
                        style={{ fontSize: '11px', color: '#E05555', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px' }}
                        title="Clear override"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </Section>

      {/* ── Network Topology (Optional, M6.2) ─────────────────────────────── */}
      <Section title="Network Topology — Optional">
        <p style={{ fontSize: '12px', color: '#8FA4B5', marginTop: 0, marginBottom: '12px' }}>
          Used by DC Topology and Fabric Layout diagrams (M6.2). Leave blank to omit those diagrams.
        </p>

        <Field label="Firewall Count" tooltip="Number of physical firewall appliances. Used by the DC Topology and Fabric Layout diagrams. Leave blank to omit those diagrams from the output.">
          <input
            type="number"
            min={0}
            max={16}
            step={1}
            placeholder="e.g. 2"
            value={config.firewallCount ?? ''}
            onChange={e => {
              if (e.target.value === '') {
                update({ firewallCount: undefined });
              } else {
                const n = parseInt(e.target.value, 10);
                if (!isNaN(n) && n >= 0) update({ firewallCount: n });
              }
            }}
            style={inputStyle}
          />
        </Field>

        {(config.firewallCount ?? 0) > 0 && (
          <Field label="Firewall Redundancy" tooltip="Active-Passive: one firewall is active, the other is in hot-standby. Active-Active: both firewalls process traffic simultaneously.">
            <RadioGroup<RedundancyModel>
              name="firewallRedundancy"
              value={config.firewallRedundancy ?? 'active_passive'}
              options={[
                { value: 'active_passive', label: 'Active-Passive' },
                { value: 'active_active', label: 'Active-Active' },
              ]}
              onChange={v => update({ firewallRedundancy: v })}
            />
          </Field>
        )}

        <Field label="Border Leaf Count" tooltip="Number of border leaf switches connecting the datacenter fabric to external networks or WAN uplinks. Used by the Leaf-Spine Fabric diagram.">
          <input
            type="number"
            min={0}
            max={8}
            step={1}
            placeholder="e.g. 2"
            value={config.borderLeafCount ?? ''}
            onChange={e => {
              if (e.target.value === '') {
                update({ borderLeafCount: undefined });
              } else {
                const n = parseInt(e.target.value, 10);
                if (!isNaN(n) && n >= 0) update({ borderLeafCount: n });
              }
            }}
            style={inputStyle}
          />
        </Field>

        <Field label="Uplink Provider Count" tooltip="Number of external WAN or internet uplinks. Used for the DC Topology diagram to show provider diversity.">
          <input
            type="number"
            min={0}
            max={8}
            step={1}
            placeholder="e.g. 2"
            value={config.uplinkProviderCount ?? ''}
            onChange={e => {
              if (e.target.value === '') {
                update({ uplinkProviderCount: undefined });
              } else {
                const n = parseInt(e.target.value, 10);
                if (!isNaN(n) && n >= 0) update({ uplinkProviderCount: n });
              }
            }}
            style={inputStyle}
          />
        </Field>

        <Field label="DCI Mechanism" tooltip="Datacenter Interconnect technology used between Primary and DR sites. Shown on the DC Topology diagram. Does not affect capacity sizing calculations.">
          <select
            value={config.dciMechanism ?? 'none'}
            onChange={e => {
              const v = e.target.value as SizingConfig['dciMechanism'];
              update({ dciMechanism: v === 'none' ? undefined : v });
            }}
            style={selectStyle}
          >
            <option value="none">None</option>
            <option value="mpls">MPLS</option>
            <option value="ipsec_overlay">IPsec Overlay</option>
            <option value="dark_fibre">Dark Fibre</option>
          </select>
        </Field>
      </Section>
    </div>
  );
}
